import { useState, useEffect } from "react";
import { DIAL_CODES, getStatesForCountry, getLgasForState } from "../utils/phone.js";
import { toSlug } from "../utils/slug.js";
import { StatementSurvey } from "./StatementSurvey.jsx";
import { apiFetch } from "../services/api.js";
import { F, L } from "../styles/theme.js";
import { ELECTORAL_POSITIONS, ROLE_GEO_RULES, MULTI_LGA_ROLES, validateRoleGeo } from "@qm/shared/constants";

// Re-export for backward compatibility (other files may import from here)
export { ROLE_GEO_RULES, validateRoleGeo, MULTI_LGA_ROLES };

export function CreatePersonaStep1({ form, setForm, statementMode, setStatementMode, kbDrafts, prefilling, prefillErr, ok, onAiFill, onNext, validating, validationErr }) {
  const selCountry = DIAL_CODES.find(d => d.code === form.geoCountry);
  const stateList  = form.geoCountry ? getStatesForCountry(form.geoCountry) : [];
  const lgaList    = (form.geoCountry && form.geoState) ? getLgasForState(form.geoCountry, form.geoState) : [];
  const isStandardRole = ELECTORAL_POSITIONS.includes(form.role);
  const isMultiLga = MULTI_LGA_ROLES.includes(form.role);
  const roleGeoErr = isStandardRole ? validateRoleGeo(form.role, form.geoCountry, form.geoState, form.geoLga, form.geoLgas) : null;

  // HF-7: slug availability check
  const slug = form.name ? toSlug(form.name) : "";
  const [slugStatus, setSlugStatus] = useState(null); // null | "checking" | "available" | "taken"
  useEffect(() => {
    if (!slug) { setSlugStatus(null); return; }
    setSlugStatus("checking");
    const t = setTimeout(() => {
      apiFetch(`/api/admin/check-slug/${slug}`)
        .then(d => setSlugStatus(d.available ? "available" : "taken"))
        .catch(() => setSlugStatus(null));
    }, 500);
    return () => clearTimeout(t);
  }, [slug]);

  // ADMIN-11: validation errors (role+geo, slug, etc.) passed from parent via validateOnly

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Name */}
      <div>
        <label style={L}>Full Name *</label>
        <input style={F} placeholder={form.personaType === "agency" ? "e.g. Niger Delta Development Commission (NDDC)" : "e.g. Senator Jane Smith"} value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        {form.name && (
          <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace" }}>
            <span style={{ color: "#475569" }}>🔗 {(import.meta.env.VITE_APP_URL ?? "").replace(/\/$/, "").replace(/^https?:\/\//, "") || (typeof window !== "undefined" ? window.location.origin.replace(/^https?:\/\//, "") : "questionme.io")}/</span>
            <span style={{ color: "#3b82f6" }}>{toSlug(form.name)}</span>
            {slugStatus === "checking" && <span style={{ color: "#64748b", marginLeft: 8 }}>⟳</span>}
            {slugStatus === "available" && <span style={{ color: "#22c55e", marginLeft: 8 }}>✓ available</span>}
            {slugStatus === "taken" && <span style={{ color: "#ef4444", marginLeft: 8 }}>⚠ already taken</span>}
          </div>
        )}
      </div>

      {/* v1.28.2 — short name for voter-facing CTA ("Ask NDDC a Question"
          vs the word-split fallback that picks "Delta" from "Niger Delta
          Development Commission"). Optional; blank → client falls back to
          the heuristic at render time. */}
      <div>
        <label style={L}>
          Short Name <span style={{ color: "#334155", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>(optional — used in "Ask ___ a Question")</span>
        </label>
        <input
          style={F}
          placeholder={form.personaType === "agency" ? "e.g. NDDC" : "e.g. President Tinubu"}
          value={form.shortName ?? ""}
          onChange={e => setForm(f => ({ ...f, shortName: e.target.value }))}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          {/* v1.28.0 (AGCY): for agencies, the electoral-position dropdown is
              replaced with a free-form "Agency Type" text input. Agencies
              don't have electoral positions — their role is a descriptive
              label like "State Ministry" or "Federal Commission" that feeds
              the AI prompt as institutional context. */}
          {form.personaType === "agency" ? (
            <>
              <label style={L}>Agency Type *</label>
              <input style={F} placeholder="e.g. State Ministry, Federal Commission"
                value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} />
            </>
          ) : (
            <>
              <label style={L}>Electoral Position *</label>
              <div style={{ position: "relative" }}>
                <select className="qm-sel" value={ELECTORAL_POSITIONS.includes(form.role) ? form.role : (form.role ? "__other__" : "")}
                  onChange={e => {
                    const v = e.target.value;
                    if (v === "__other__") setForm(f => ({ ...f, role: "" }));
                    else setForm(f => ({ ...f, role: v }));
                  }}>
                  <option value="">Select position...</option>
                  {ELECTORAL_POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="__other__">Other (custom)</option>
                </select>
                <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
              </div>
              {(!ELECTORAL_POSITIONS.includes(form.role) && (form.role || form.role === "")) && form.role !== "" && (
                <input style={{ ...F, marginTop: 8 }} placeholder="e.g. Mayor, Councillor" value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))} />
              )}
              {/* Show custom input when "Other" is selected and role is empty */}
              {!ELECTORAL_POSITIONS.includes(form.role) && form.role === "" && (
                <input style={{ ...F, marginTop: 8 }} placeholder="e.g. Mayor, Councillor" value=""
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))} />
              )}
            </>
          )}
        </div>
        <div>
          <label style={L}>Display Location</label>
          <input style={F} placeholder="e.g. Abuja, Nigeria" value={form.location}
            onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
        </div>
      </div>

      {/* ADMIN-9: "Generate from public data" removed — KB must come from admin-provided links */}

      {/* v1.27.0: "Party" text input removed. The colour-swatch picker stays
          but is reframed as an "Accent Colour" theme choice — the admin still
          picks the persona's visual identity (avatar tint, chat accents,
          trivia timer, etc.), it's just no longer framed as party colour. */}
      <div>
        <label style={L}>Accent Colour</label>
        <div style={{ display: "flex", gap: 7, paddingTop: 5 }}>
          {["#2563eb","#dc2626","#16a34a","#7c3aed","#0891b2","#ea580c"].map(c => (
            <button key={c} onClick={() => setForm(f => ({ ...f, accentColor: c }))}
              style={{ width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer", padding: 0,
                border: form.accentColor === c ? "3px solid #fff" : "3px solid transparent",
                outline: form.accentColor === c ? `2px solid ${c}` : "none" }} />
          ))}
        </div>
      </div>

      {/* Geo scope */}
      <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#3b82f6", textTransform: "uppercase", marginBottom: 14 }}>
          Geo Scope — Who Sees This Candidate
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={L}>Country <span style={{ color: isStandardRole ? "#ef4444" : "#334155", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>{isStandardRole ? "(required for " + form.role + ")" : "(leave blank = visible to all)"}</span></label>
            <div style={{ position: "relative" }}>
              <select className="qm-sel" value={form.geoCountry}
                onChange={e => setForm(f => ({ ...f, geoCountry: e.target.value, geoState: "", geoLga: "" }))}>
                <option value="">All countries</option>
                {DIAL_CODES.map(d => <option key={d.code} value={d.code}>{d.flag} {d.country}</option>)}
              </select>
              <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
            </div>
          </div>
          {form.geoCountry && stateList.length > 0 && (
            <div>
              <label style={L}>{selCountry?.stateLabel || "State"} <span style={{ color: ROLE_GEO_RULES[form.role]?.state ? "#ef4444" : "#334155", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>{ROLE_GEO_RULES[form.role]?.state ? "(required)" : "(optional)"}</span></label>
              <div style={{ position: "relative" }}>
                <select className="qm-sel" value={form.geoState}
                  onChange={e => setForm(f => ({ ...f, geoState: e.target.value, geoLga: "" }))}>
                  <option value="">All {selCountry?.stateLabel || "states"}</option>
                  {stateList.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
              </div>
            </div>
          )}
          {form.geoState && lgaList.length > 0 && (
            <div>
              <label style={L}>
                {selCountry?.lgaLabel || "Local Area"}
                {isMultiLga && <span style={{ color: "#3b82f6", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}> (select multiple)</span>}
                {!isMultiLga && <span style={{ color: ROLE_GEO_RULES[form.role]?.lga ? "#ef4444" : "#334155", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>{ROLE_GEO_RULES[form.role]?.lga ? " (required)" : " (optional)"}</span>}
              </label>
              {isMultiLga ? (
                /* ADMIN-6: Multi-LGA checkbox selection */
                <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #1e2d45", borderRadius: 9, background: "#0f1623", padding: "8px 10px" }}>
                  {lgaList.map(l => (
                    <label key={l} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", cursor: "pointer", fontSize: 13, color: "#e2e8f0" }}>
                      <input type="checkbox" checked={(form.geoLgas ?? []).includes(l)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setForm(f => ({
                            ...f,
                            geoLgas: checked ? [...(f.geoLgas ?? []), l] : (f.geoLgas ?? []).filter(x => x !== l),
                            geoLga: "", // clear single LGA when using multi
                          }));
                        }}
                        style={{ accentColor: "#3b82f6" }} />
                      {l}
                    </label>
                  ))}
                  {(form.geoLgas ?? []).length > 0 && (
                    <div style={{ fontSize: 10, color: "#60a5fa", marginTop: 6 }}>
                      {(form.geoLgas ?? []).length} selected
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <select className="qm-sel" value={form.geoLga}
                    onChange={e => setForm(f => ({ ...f, geoLga: e.target.value }))}>
                    <option value="">All {selCountry?.lgaLabel || "areas"}</option>
                    {lgaList.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", color: "#475569", pointerEvents: "none", fontSize: 11 }}>▾</span>
                </div>
              )}
            </div>
          )}
          {form.geoCountry && (
            <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.6, padding: "8px 10px", background: "#090e1a", borderRadius: 7 }}>
              👁 Visible to: <strong style={{ color: "#94a3b8" }}>
                {[
                  DIAL_CODES.find(d => d.code === form.geoCountry)?.country,
                  form.geoState || null,
                  isMultiLga && (form.geoLgas ?? []).length > 0 ? `${(form.geoLgas ?? []).length} LGAs` : (form.geoLga || null),
                ].filter(Boolean).join(" → ")}
              </strong> voters
            </div>
          )}
        </div>
      </div>

      {/* Bio */}
      <div>
        <label style={L}>Profile Bio <span style={{ color: "#334155", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 10 }}>(shown publicly)</span></label>
        <textarea style={{ ...F, minHeight: 90, resize: "vertical", lineHeight: 1.6 }}
          placeholder="Public-facing bio — background, career highlights..."
          value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
      </div>

      {/* AI Statement */}
      <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #1e2d45", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#3b82f6", textTransform: "uppercase", marginBottom: 2 }}>AI Personal Statement</div>
            <div style={{ fontSize: 11, color: "#475569" }}>Trains the clone — choose how to build it</div>
          </div>
          <div style={{ display: "flex", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 9, padding: 3, gap: 2, flexShrink: 0 }}>
            {[["survey","🧠 Survey"],["manual","✍️ Manual"]].map(([mode, label]) => (
              <button key={mode} onClick={() => setStatementMode(mode)}
                style={{ padding: "6px 12px", borderRadius: 7, border: "none", fontFamily: "inherit", whiteSpace: "nowrap",
                  background: statementMode === mode ? "#1e3a5f" : "transparent",
                  color: statementMode === mode ? "#60a5fa" : "#475569",
                  fontSize: 11, fontWeight: statementMode === mode ? 700 : 400, cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ padding: 16 }}>
          {statementMode === "survey" && !form.surveyStatement && (
            <StatementSurvey form={form}
              onComplete={stmt => { setForm(f => ({ ...f, surveyStatement: stmt, statement: stmt })); setStatementMode("preview"); }}
              onCancel={() => setStatementMode("manual")} />
          )}
          {statementMode === "preview" && form.surveyStatement && (
            <div style={{ animation: "fadeUp .3s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 16 }}>✅</span>
                <span style={{ color: "#22c55e", fontSize: 13, fontWeight: 700 }}>AI statement compiled from survey</span>
              </div>
              <div style={{ background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 9, padding: 12, maxHeight: 180, overflowY: "auto", marginBottom: 12 }}>
                <pre style={{ color: "#64748b", fontSize: 11, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>{form.surveyStatement}</pre>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setForm(f => ({ ...f, surveyStatement: "", statement: "" })); setStatementMode("survey"); }}
                  style={{ flex: 1, padding: "8px 0", background: "transparent", border: "1px solid #1e2d45", borderRadius: 8, color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  🔄 Redo survey
                </button>
                <button onClick={() => setStatementMode("manual")}
                  style={{ flex: 1, padding: "8px 0", background: "transparent", border: "1px solid #1e2d45", borderRadius: 8, color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  ✏️ Add manual context
                </button>
              </div>
            </div>
          )}
          {statementMode === "manual" && (
            <div style={{ animation: "fadeUp .2s ease" }}>
              {form.surveyStatement && (
                <div style={{ marginBottom: 8, padding: "7px 10px", background: "#22c55e11", border: "1px solid #22c55e30", borderRadius: 7, fontSize: 11, color: "#22c55e" }}>
                  ✓ Survey statement is included — add additional context here
                </div>
              )}
              {/* ADMIN-9: File upload for personal statement */}
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <label style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
                  background: "#3b82f612", border: "1px solid #3b82f630", borderRadius: 8,
                  color: "#60a5fa", fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}>
                  📄 Upload Statement File
                  <input type="file" accept=".txt,.pdf,.doc,.docx" style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { alert("File too large (max 5MB)"); return; }
                      try {
                        const text = await file.text();
                        setForm(f => ({ ...f, manualStatement: text.slice(0, 5000) }));
                      } catch {
                        alert("Could not read file");
                      }
                    }} />
                </label>
                <span style={{ fontSize: 11, color: "#475569" }}>.txt, .pdf, .docx (max 5MB)</span>
              </div>
              <textarea style={{ ...F, minHeight: 160, resize: "vertical", lineHeight: 1.6, background: "#090e1a" }}
                placeholder="Detailed values, policy positions, communication style, background..."
                value={form.manualStatement} onChange={e => setForm(f => ({ ...f, manualStatement: e.target.value }))} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                <span style={{ fontSize: 11, color: "#475569" }}>{(form.surveyStatement.length + form.manualStatement.length)} chars total</span>
                {!form.manualStatement && !form.surveyStatement && (
                  <button onClick={() => setStatementMode("survey")}
                    style={{ background: "none", border: "none", color: "#3b82f6", fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                    Use survey instead →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {(form.surveyStatement || form.manualStatement || form.statement) && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 12px", background: "#16a34a12", border: "1px solid #16a34a30", borderRadius: 9, fontSize: 12, color: "#22c55e" }}>
          ✓ Statement ready — {(form.surveyStatement.length + (form.manualStatement || form.statement).length)} characters
        </div>
      )}

      {roleGeoErr && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 12px", background: "#ef444412", border: "1px solid #ef444430", borderRadius: 9, fontSize: 12, color: "#ef4444" }}>
          ⚠ {roleGeoErr}
        </div>
      )}

      {validationErr && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 12px", background: "#ef444412", border: "1px solid #ef444430", borderRadius: 9, fontSize: 12, color: "#ef4444" }}>
          ⚠ {validationErr}
        </div>
      )}

      <button onClick={onNext} disabled={!ok || !!roleGeoErr || slugStatus === "taken" || validating}
        style={{ padding: "14px",
          background: (!ok || roleGeoErr || slugStatus === "taken") ? "#1e2d45" : "linear-gradient(135deg,#2563eb,#1d4ed8)",
          color: (!ok || roleGeoErr || slugStatus === "taken") ? "#475569" : "#fff",
          border: "none", borderRadius: 11, fontSize: 14, fontWeight: 700,
          cursor: (!ok || roleGeoErr || slugStatus === "taken" || validating) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
        {validating ? "Validating…" : "✦ Generate Clone"}
      </button>
    </div>
  );
}
